/**
 * The room dressed for an occasion: a garland along the top of the wall and a thing or two on the
 * floor. Pure decoration, drawn behind the people, and nothing here is clickable.
 *
 * Callers say where the floor has room (`spots`), because only they know where the desks, the jar
 * and the boards are.
 */

import { memo, type ReactNode } from 'react'
import type { Holiday } from './scenery'

const INK = '#1a1f2c'

type GarlandKind = 'bulbs' | 'flags' | 'hearts' | 'leaves' | 'none'

interface Dressing {
  garland: GarlandKind
  colors: string[]
  /** What stands on the floor, at each spot in turn. */
  props: Array<(x: number, y: number) => ReactNode>
  /** Hung from the ceiling instead of a garland. */
  hanging?: 'lanterns' | 'bats'
}

const DRESSING: Record<Holiday, Dressing> = {
  christmas: { garland: 'bulbs', colors: ['#E5484D', '#F5C542', '#46A758', '#3E9BE6'], props: [(x, y) => <Tree x={x} y={y} />, (x, y) => <Gifts x={x} y={y} />] },
  newYear: { garland: 'flags', colors: ['#F5C542', '#C9D2DF', '#F5C542', '#8E7CC3'], props: [(x, y) => <Confetti x={x} y={y} />, (x, y) => <Confetti x={x} y={y} seed={5} />] },
  easter: { garland: 'flags', colors: ['#F7A8C0', '#A8D8F7', '#F7E3A8', '#B8F2C9'], props: [(x, y) => <Eggs x={x} y={y} />, (x, y) => <Eggs x={x} y={y} seed={2} />] },
  halloween: { garland: 'flags', colors: ['#F28C28', '#2B1D3A', '#8E44AD'], props: [(x, y) => <Pumpkin x={x} y={y} />, (x, y) => <Pumpkin x={x} y={y} small />], hanging: 'bats' },
  valentines: { garland: 'hearts', colors: ['#E5484D', '#F7A8C0', '#FF6F91'], props: [] },
  carnival: { garland: 'flags', colors: ['#E5484D', '#F5C542', '#46A758', '#3E9BE6', '#8E44AD'], props: [(x, y) => <Confetti x={x} y={y} />, (x, y) => <Confetti x={x} y={y} seed={9} />] },
  lunarNewYear: { garland: 'none', colors: ['#D7263D', '#F5C542'], props: [], hanging: 'lanterns' },
  diwali: { garland: 'flags', colors: ['#F28C28', '#F5C542', '#E5484D'], props: [(x, y) => <Diyas x={x} y={y} />, (x, y) => <Diyas x={x} y={y} />] },
  womensDay: { garland: 'leaves', colors: ['#B07CD8', '#8E5DB8'], props: [(x, y) => <Flowers x={x} y={y} />] },
  earthDay: { garland: 'leaves', colors: ['#46A758', '#2F8F5B'], props: [(x, y) => <Globe x={x} y={y} />] },
  programmersDay: { garland: 'flags', colors: ['#1E3A5F', '#2F9E68'], props: [(x, y) => <Terminal x={x} y={y} />] },
}

export const HolidayDecor = memo(function HolidayDecor({
  holiday,
  x,
  width,
  spots,
}: {
  holiday: Holiday | null
  x: number
  width: number
  /** Where on the floor there is room for something, near the wall. */
  spots: Array<{ x: number; y: number }>
}) {
  if (!holiday) return null
  const dressing = DRESSING[holiday]

  return (
    <g aria-hidden="true" pointerEvents="none">
      {dressing.garland !== 'none' && <Garland x={x} width={width} y={14} kind={dressing.garland} colors={dressing.colors} text={holiday === 'programmersDay'} />}
      {dressing.hanging === 'lanterns' && <Lanterns x={x} width={width} />}
      {dressing.hanging === 'bats' && <Bats x={x} width={width} />}
      {spots.map((spot, i) => {
        const draw = dressing.props[i % Math.max(1, dressing.props.length)]
        return draw && i < dressing.props.length + 1 ? <g key={i}>{draw(spot.x, spot.y)}</g> : null
      })}
    </g>
  )
})

/* ------------------------------------------------------------------ garland */

function Garland({ x, width, y, kind, colors, text }: { x: number; width: number; y: number; kind: GarlandKind; colors: string[]; text?: boolean }) {
  const spans = Math.max(2, Math.round(width / 220))
  const span = width / spans
  const sag = 16
  const items: ReactNode[] = []
  let n = 0

  for (let s = 0; s < spans; s++) {
    const ax = x + s * span
    const bx = ax + span
    const cx = (ax + bx) / 2
    const cy = y + sag * 2
    const count = Math.max(4, Math.round(span / 34))

    for (let k = 1; k < count; k++) {
      const t = k / count
      const px = (1 - t) ** 2 * ax + 2 * (1 - t) * t * cx + t ** 2 * bx
      const py = (1 - t) ** 2 * y + 2 * (1 - t) * t * cy + t ** 2 * y
      const color = colors[n % colors.length]!
      const key = `${s}-${k}`

      if (kind === 'bulbs') {
        items.push(
          <g key={key} className="bh-bulb" style={{ animationDelay: `${(n % 4) * 0.6}s` }}>
            <rect x={px - 1.6} y={py} width="3.2" height="3" fill="#3B4658" />
            <ellipse cx={px} cy={py + 7} rx="3.6" ry="5" fill={color} stroke={INK} strokeWidth="0.8" />
            <circle cx={px} cy={py + 7} r="8" fill={color} opacity="0.18" />
          </g>,
        )
      } else if (kind === 'flags') {
        items.push(
          <g key={key}>
            <path d={`M ${px - 8} ${py} L ${px + 8} ${py} L ${px} ${py + 15} Z`} fill={color} stroke={INK} strokeWidth="0.8" strokeLinejoin="round" />
            {text && <text x={px} y={py + 8} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#E8ECF4" fontFamily="var(--font-mono)">{n % 3 === 2 ? '</>' : n % 2}</text>}
          </g>,
        )
      } else if (kind === 'hearts') {
        items.push(<Heart key={key} x={px} y={py + 6} size={6} color={color} />)
      } else if (kind === 'leaves') {
        items.push(
          <ellipse key={key} cx={px} cy={py + 5} rx="4" ry="7" fill={color} stroke={INK} strokeWidth="0.7" transform={`rotate(${n % 2 ? 30 : -30} ${px} ${py + 5})`} />,
        )
      }
      n++
    }

    items.unshift(
      <path key={`w${s}`} d={`M ${ax} ${y} Q ${cx} ${cy} ${bx} ${y}`} fill="none" stroke="#2A3346" strokeWidth="1.4" />,
    )
  }

  return <g>{items}</g>
}

function Heart({ x, y, size, color }: { x: number; y: number; size: number; color: string }) {
  const s = size
  return (
    <path
      d={`M ${x} ${y + s * 0.9} C ${x - s * 1.4} ${y} ${x - s * 0.8} ${y - s * 0.9} ${x} ${y - s * 0.25} C ${x + s * 0.8} ${y - s * 0.9} ${x + s * 1.4} ${y} ${x} ${y + s * 0.9} Z`}
      fill={color}
      stroke={INK}
      strokeWidth="0.8"
    />
  )
}

/* ------------------------------------------------------------------ hanging */

function Lanterns({ x, width }: { x: number; width: number }) {
  const count = Math.max(3, Math.round(width / 260))
  return (
    <g>
      {Array.from({ length: count }, (_, i) => {
        const lx = x + (width / count) * (i + 0.5)
        const drop = 26 + (i % 2) * 16
        return (
          <g key={i} className="bh-lantern" style={{ animationDelay: `${i * 0.7}s` }}>
            <line x1={lx} y1={0} x2={lx} y2={drop} stroke="#2A3346" strokeWidth="1.2" />
            <rect x={lx - 5} y={drop - 2} width="10" height="4" fill="#F5C542" stroke={INK} strokeWidth="0.8" />
            <ellipse cx={lx} cy={drop + 14} rx="15" ry="13" fill="#D7263D" stroke={INK} strokeWidth="1.2" />
            <path d={`M ${lx - 9} ${drop + 4} Q ${lx - 13} ${drop + 14} ${lx - 9} ${drop + 24} M ${lx} ${drop + 2} L ${lx} ${drop + 26} M ${lx + 9} ${drop + 4} Q ${lx + 13} ${drop + 14} ${lx + 9} ${drop + 24}`} fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
            <rect x={lx - 5} y={drop + 25} width="10" height="4" fill="#F5C542" stroke={INK} strokeWidth="0.8" />
            <line x1={lx} y1={drop + 29} x2={lx} y2={drop + 40} stroke="#F5C542" strokeWidth="1.6" />
          </g>
        )
      })}
    </g>
  )
}

function Bats({ x, width }: { x: number; width: number }) {
  const count = Math.max(2, Math.round(width / 330))
  return (
    <g>
      {Array.from({ length: count }, (_, i) => {
        const bx = x + (width / count) * (i + 0.35)
        const by = 30 + (i % 2) * 18
        return (
          <path
            key={i}
            className="bh-lantern"
            d={`M ${bx} ${by} q -6 -7 -14 -3 q 4 2 3 6 q -4 -2 -7 1 q 7 1 10 6 q 4 -3 8 -4 q 4 1 8 4 q 3 -5 10 -6 q -3 -3 -7 -1 q -1 -4 3 -6 q -8 -4 -14 3 Z`}
            fill="#1B1426"
            stroke="#3A2A4F"
            strokeWidth="0.8"
          />
        )
      })}
    </g>
  )
}

/* ------------------------------------------------------------------ on the floor */

function Tree({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="30" ry="6" fill="rgba(0,0,0,0.25)" />
      <rect x="-6" y="-14" width="12" height="14" fill="#6B4226" stroke={INK} strokeWidth="1.2" />
      {[
        [0, -14, 34, 26],
        [0, -34, 28, 24],
        [0, -52, 21, 21],
        [0, -67, 14, 17],
      ].map(([cx, base, half, h], i) => (
        <path key={i} d={`M ${cx! - half!} ${base} L ${cx} ${base! - h!} L ${cx! + half!} ${base} Z`} fill={i % 2 ? '#2F8F5B' : '#277A4C'} stroke={INK} strokeWidth="1.3" strokeLinejoin="round" />
      ))}
      {[[-14, -20, '#E5484D'], [12, -26, '#F5C542'], [-6, -40, '#3E9BE6'], [9, -46, '#E5484D'], [-4, -58, '#F5C542'], [4, -30, '#E8ECF4']].map(([bx, by, c], i) => (
        <circle key={i} className="bh-bulb" style={{ animationDelay: `${i * 0.4}s` }} cx={bx as number} cy={by as number} r="2.6" fill={c as string} stroke={INK} strokeWidth="0.6" />
      ))}
      <path d="M 0 -91 L 2.6 -85 L 9 -85 L 4 -81 L 6 -75 L 0 -78.5 L -6 -75 L -4 -81 L -9 -85 L -2.6 -85 Z" fill="#F5C542" stroke={INK} strokeWidth="0.9" />
    </g>
  )
}

function Gifts({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {[[-14, 0, 18, 14, '#E5484D', '#F5C542'], [4, 0, 14, 11, '#3E9BE6', '#E8ECF4'], [-4, -13, 12, 10, '#46A758', '#E5484D']].map(([gx, gy, w, h, fill, ribbon], i) => (
        <g key={i}>
          <rect x={gx as number} y={(gy as number) - (h as number)} width={w as number} height={h as number} fill={fill as string} stroke={INK} strokeWidth="1.1" />
          <rect x={(gx as number) + (w as number) / 2 - 1.5} y={(gy as number) - (h as number)} width="3" height={h as number} fill={ribbon as string} />
        </g>
      ))}
    </g>
  )
}

function Eggs({ x, y, seed = 0 }: { x: number; y: number; seed?: number }) {
  const colors = ['#F7A8C0', '#A8D8F7', '#F7E3A8', '#B8F2C9', '#D7B8F2']
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="3" rx="30" ry="5" fill="rgba(0,0,0,0.2)" />
      {[-18, 0, 17].map((ex, i) => {
        const c = colors[(i + seed) % colors.length]!
        return (
          <g key={i} transform={`rotate(${(i - 1) * 12} ${ex} -8)`}>
            <ellipse cx={ex} cy="-8" rx="8" ry="10.5" fill={c} stroke={INK} strokeWidth="1.2" />
            <path d={`M ${ex - 7.5} -9 Q ${ex - 4} -12 ${ex} -9 Q ${ex + 4} -6 ${ex + 7.5} -9`} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.3" />
            <path d={`M ${ex - 6} -3 L ${ex + 6} -3`} stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" strokeDasharray="2 2" />
          </g>
        )
      })}
    </g>
  )
}

function Pumpkin({ x, y, small }: { x: number; y: number; small?: boolean }) {
  const s = small ? 0.75 : 1
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx="0" cy="2" rx="24" ry="5" fill="rgba(0,0,0,0.25)" />
      <path d="M -2 -30 Q 0 -38 6 -40" fill="none" stroke="#4A6B2A" strokeWidth="3.4" strokeLinecap="round" />
      <ellipse cx="-9" cy="-14" rx="12" ry="15" fill="#E07A1F" stroke={INK} strokeWidth="1.3" />
      <ellipse cx="9" cy="-14" rx="12" ry="15" fill="#E07A1F" stroke={INK} strokeWidth="1.3" />
      <ellipse cx="0" cy="-14" rx="11" ry="16" fill="#F28C28" stroke={INK} strokeWidth="1.3" />
      <path d="M -11 -18 L -6 -23 L -2 -18 Z M 2 -18 L 6 -23 L 11 -18 Z" fill="#FFD166" stroke={INK} strokeWidth="0.8" />
      <path d="M -11 -8 Q -5 -2 0 -7 Q 5 -2 11 -8 Q 5 -4 0 -3 Q -5 -4 -11 -8 Z" fill="#FFD166" stroke={INK} strokeWidth="0.8" />
    </g>
  )
}

function Confetti({ x, y, seed = 1 }: { x: number; y: number; seed?: number }) {
  const colors = ['#E5484D', '#F5C542', '#46A758', '#3E9BE6', '#8E44AD', '#FF6F91']
  let s = seed * 97
  const next = () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
  return (
    <g transform={`translate(${x} ${y})`}>
      {Array.from({ length: 22 }, (_, i) => {
        const cx = (next() - 0.5) * 120
        const cy = next() * 60 - 10
        return (
          <rect
            key={i}
            x={cx - 2}
            y={cy - 1.1}
            width="4"
            height="2.2"
            fill={colors[i % colors.length]}
            transform={`rotate(${Math.round(next() * 180)} ${cx} ${cy})`}
          />
        )
      })}
    </g>
  )
}

function Diyas({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {[-22, 0, 22].map((dx, i) => (
        <g key={i}>
          <circle cx={dx} cy="-9" r="9" fill="#F5C542" opacity="0.18" />
          <path d={`M ${dx - 8} -4 Q ${dx} 4 ${dx + 8} -4 Z`} fill="#C2560B" stroke={INK} strokeWidth="1" />
          <path className="bh-flame" style={{ animationDelay: `${i * 0.3}s` }} d={`M ${dx} -14 Q ${dx + 3.2} -8 ${dx} -5 Q ${dx - 3.2} -8 ${dx} -14 Z`} fill="#FFB02E" stroke="#E5484D" strokeWidth="0.6" />
        </g>
      ))}
    </g>
  )
}

function Flowers({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="16" ry="4" fill="rgba(0,0,0,0.25)" />
      {[[-10, -44], [0, -50], [10, -43], [-4, -38], [6, -36]].map(([fx, fy], i) => (
        <g key={i}>
          <path d={`M 0 -18 Q ${fx! / 2} ${(fy! - 18) / 2} ${fx} ${fy}`} fill="none" stroke="#2F8F5B" strokeWidth="1.6" />
          {[0, 72, 144, 216, 288].map((a) => (
            <circle key={a} cx={fx! + Math.cos((a * Math.PI) / 180) * 3.6} cy={fy! + Math.sin((a * Math.PI) / 180) * 3.6} r="2.8" fill={i % 2 ? '#B07CD8' : '#E0A3F0'} stroke={INK} strokeWidth="0.5" />
          ))}
          <circle cx={fx} cy={fy} r="2" fill="#F5C542" />
        </g>
      ))}
      <path d="M -9 -20 L 9 -20 L 7 0 L -7 0 Z" fill="#8E5DB8" stroke={INK} strokeWidth="1.3" strokeLinejoin="round" />
    </g>
  )
}

function Globe({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="18" ry="4" fill="rgba(0,0,0,0.25)" />
      <path d="M -10 0 L 10 0 L 4 -8 L -4 -8 Z" fill="#6B4226" stroke={INK} strokeWidth="1.1" />
      <line x1="0" y1="-8" x2="0" y2="-14" stroke={INK} strokeWidth="2" />
      <circle cx="0" cy="-30" r="16" fill="#3E9BE6" stroke={INK} strokeWidth="1.4" />
      <path d="M -10 -38 Q -4 -42 0 -36 Q 4 -31 -2 -28 Q -8 -26 -10 -32 Z M 4 -24 Q 10 -26 12 -20 Q 8 -16 4 -19 Z M 6 -40 Q 11 -40 12 -34 Q 8 -33 6 -36 Z" fill="#46A758" stroke={INK} strokeWidth="0.7" />
      <path d="M -18 -30 A 18 18 0 0 1 18 -30" fill="none" stroke="#C9A227" strokeWidth="1.6" />
    </g>
  )
}

function Terminal({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="22" ry="4" fill="rgba(0,0,0,0.25)" />
      <rect x="-20" y="-34" width="40" height="30" rx="3" fill="#0F1C2E" stroke={INK} strokeWidth="1.3" />
      <text x="0" y="-15" textAnchor="middle" fontSize="11" fontWeight="700" fill="#5CE1A6" fontFamily="var(--font-mono)">{'</>'}</text>
      <rect x="-4" y="-4" width="8" height="4" fill="#3B4658" />
    </g>
  )
}
